import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Sparkles, Briefcase, ArrowRight, ChevronRight, ExternalLink } from 'lucide-react';

export type PlansIntent = 'participar' | 'constelacao' | 'terapeuta';

const WHATSAPP_AVISOS_GRUPO =
  'https://chat.whatsapp.com/DsJxwW1i4IJ6pernEwmtQC';

interface PathCardProps {
  to?: string;
  href?: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  accent: 'green' | 'purple' | 'teal';
  badge?: string;
  ctaLabel?: string;
}

const accentStyles = {
  green: {
    ring: 'hover:ring-[#7ED99C]/50',
    iconBg: 'bg-[#7ED99C]/15 text-[#3d8f5c]',
    gradient: 'from-[#7ED99C]/10 to-transparent',
    cta: 'text-[#3d8f5c] group-hover:text-[#2d6f45]',
  },
  purple: {
    ring: 'hover:ring-[#A56096]/50',
    iconBg: 'bg-[#A56096]/15 text-[#A56096]',
    gradient: 'from-[#A56096]/10 to-transparent',
    cta: 'text-[#A56096] group-hover:text-[#8a4f7f]',
  },
  teal: {
    ring: 'hover:ring-[#4A7C75]/50',
    iconBg: 'bg-[#4A7C75]/15 text-[#4A7C75]',
    gradient: 'from-[#4A7C75]/10 to-transparent',
    cta: 'text-[#4A7C75] group-hover:text-[#3a6560]',
  },
};

const cardClassName = (ring: string) =>
  `group relative flex flex-col rounded-3xl bg-white p-8 shadow-lg ring-1 ring-black/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${ring} focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#4A7C75]`;

const PathCard: React.FC<PathCardProps> = ({
  to,
  href,
  icon,
  title,
  description,
  accent,
  badge,
  ctaLabel = 'Clique aqui',
}) => {
  const s = accentStyles[accent];
  const isExternal = Boolean(href);

  const inner = (
    <>
      <div
        className={`pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-br ${s.gradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
        aria-hidden
      />
      <div className="relative flex flex-1 flex-col">
        {badge && (
          <span className="mb-4 inline-flex w-fit rounded-full bg-[#EAF4F2] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#4A7C75]">
            {badge}
          </span>
        )}
        <div className={`mb-6 flex h-14 w-14 items-center justify-center rounded-2xl ${s.iconBg}`}>
          {icon}
        </div>
        <h2 className="mb-3 text-xl font-bold leading-snug text-[#3A3157] sm:text-2xl">{title}</h2>
        <p className="mb-8 flex-1 text-base leading-relaxed text-[#4A7C75]/90">{description}</p>
        <span className={`inline-flex items-center gap-2 text-sm font-semibold ${s.cta}`}>
          {ctaLabel}
          {isExternal ? (
            <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" aria-hidden />
          )}
        </span>
      </div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cardClassName(s.ring)}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link to={to || '/planos'} className={cardClassName(s.ring)}>
      {inner}
    </Link>
  );
};

const PlansHub: React.FC = () => {
  return (
    <section className="mb-16" aria-labelledby="plans-hub-heading">
      <div className="mx-auto max-w-4xl text-center">
        <h1
          id="plans-hub-heading"
          className="mb-12 inline-block max-w-3xl text-left text-2xl font-bold leading-snug tracking-tight md:text-3xl lg:text-4xl"
          style={{ color: '#3A5859' }}
        >
          <span className="block">Escolha, abaixo, se você quer:</span>
          <span className="mt-4 block space-y-2 text-left text-xl md:text-2xl lg:text-3xl">
            <span className="block" style={{ color: '#A56096' }}>
              Participar/Conhecer
            </span>
            <span className="block" style={{ color: '#A56096' }}>
              Fazer a sua Constelação
            </span>
            <span className="block" style={{ color: '#A56096' }}>
              Sou Constelador
            </span>
          </span>
        </h1>
      </div>

      <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-3 md:gap-8">
        {/* Profissional — planos da plataforma para consteladores */}
        <PathCard
          to="/planos?secao=terapeuta"
          icon={<Briefcase className="h-7 w-7" aria-hidden />}
          title="Planos para Consteladores"
          description="Escolha entre horas avulsas ou torne-se um constelador homologado"
          accent="teal"
          badge="Profissional"
          ctaLabel="Clique aqui"
        />

        {/* Representante — grupo WhatsApp de avisos */}
        <PathCard
          href={WHATSAPP_AVISOS_GRUPO}
          icon={<Users className="h-7 w-7" aria-hidden />}
          title="Participe de uma constelação"
          description="Assista e participe das nossas constelações."
          accent="green"
          badge="Representante"
          ctaLabel="Clique aqui e entre no grupo de aviso dos próximos encontros"
        />

        {/* Constelado — agendar com profissionais homologados */}
        <PathCard
          to="/planos?secao=participante&intencao=constelacao"
          icon={<Sparkles className="h-7 w-7" aria-hidden />}
          title="Agende a sua Constelação"
          description="Conte com a nossa rede de Consteladores homologados pela plataforma. Todos com certificado de Formação e verificados pela plataforma."
          accent="purple"
          badge="Constelado"
          ctaLabel="Para escolher um profissional e agendar, clique aqui"
        />
      </div>

      <div className="mx-auto mt-12 flex max-w-2xl flex-col items-center gap-4 sm:flex-row sm:justify-center">
        <Link
          to="/quero-participar"
          className="inline-flex items-center gap-2 rounded-full border border-[#4A7C75]/30 bg-white px-6 py-3 text-sm font-medium text-[#4A7C75] shadow-sm transition hover:bg-[#EAF4F2]"
        >
          Ver salas abertas agora
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
        <Link
          to="/terapeutas"
          className="inline-flex items-center gap-2 rounded-full border border-[#A56096]/30 bg-white px-6 py-3 text-sm font-medium text-[#A56096] shadow-sm transition hover:bg-[#fdf8fc]"
        >
          Conhecer consteladores
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </section>
  );
};

export default PlansHub;
